import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play } from "lucide-react";
import { useT } from "@/i18n/LanguageProvider";
import AnimatedDelivery from "./AnimatedMap";
import PhoneMockup from "./PhoneMockup";
import HeroTrackWidget from "./HeroTrackWidget";

export default function Hero() {
  const t = useT();

  return (
    <section
      className="relative overflow-hidden bg-delfi-dark text-white pt-32 pb-16 md:pt-36 md:pb-20"
      data-testid="hero-section"
    >
      {/* Ghosted giant chevron motif */}
      <div
        className="chevron-bg right-[-10%] top-[-8%] animate-[chevron-drift_18s_ease-in-out_infinite]"
        aria-hidden="true"
      >
        ›
      </div>
      <div className="chevron-bg left-[-22%] bottom-[-34%] opacity-[0.05]" aria-hidden="true">
        ›
      </div>

      {/* Grid pattern */}
      <div className="absolute inset-0 grid-pattern opacity-40" aria-hidden="true" />

      {/* Red glow accents */}
      <div className="hero-glow left-[-260px] top-[-200px]" aria-hidden="true" />
      <div className="hero-glow right-[-260px] bottom-[-260px] opacity-70" aria-hidden="true" />

      {/* Red particle trails */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        {Array.from({ length: 14 }).map((_, i) => (
          <span
            key={i}
            className="particle"
            style={{
              left: `${(i * 7.2 + 5) % 100}%`,
              bottom: `${(i * 13) % 40}px`,
              animationDelay: `${(i * 0.7) % 6}s`,
              animationDuration: `${5 + (i % 4)}s`,
            }}
          />
        ))}
      </div>

      <div className="delfi-container relative z-10">
        {/* Top: text on one side, phone mockup on the other */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-12 lg:gap-16 items-center">
          {/* ----- Text column ----- */}
          <div>
            <div
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-white/15 bg-white/[0.04] backdrop-blur text-xs tracking-wider uppercase text-white/80 animate-fade-up"
              style={{ animationDelay: "0.05s" }}
              data-testid="hero-eyebrow"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-delfi-red animate-pulse" />
              {t.hero.eyebrow}
            </div>

            <h1
              className="headline-display text-white mt-6 text-[clamp(2rem,4.4vw,3.6rem)] animate-fade-up"
              style={{ animationDelay: "0.15s" }}
              data-testid="hero-headline"
            >
              <span className="block whitespace-nowrap">{t.hero.headline1}</span>
              {t.hero.headline2}{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-delfi-gold">{t.hero.highlight}</span>
                <span
                  className="absolute left-0 right-0 bottom-1 h-3 bg-delfi-gold/20 -z-0 rounded-sm"
                  aria-hidden="true"
                />
              </span>
            </h1>

            <p
              className="mt-7 text-base md:text-lg text-white/75 max-w-xl leading-relaxed animate-fade-up"
              style={{ animationDelay: "0.3s" }}
              data-testid="hero-subheadline"
            >
              {t.hero.subheadline}
            </p>

            <div
              className="mt-8 flex flex-wrap gap-4 animate-fade-up"
              style={{ animationDelay: "0.45s" }}
            >
              <Link to="/track" className="btn-delfi-primary" data-testid="hero-cta-primary">
                {t.hero.ctaPrimary} <ArrowRight size={18} />
              </Link>
              <Link to="/contact" className="btn-delfi-ghost" data-testid="hero-cta-secondary">
                <Play size={16} /> {t.hero.ctaSecondary}
              </Link>
            </div>

            {/* Compact hero track widget */}
            <div
              className="mt-7 max-w-xl animate-fade-up"
              style={{ animationDelay: "0.5s" }}
            >
              <HeroTrackWidget />
            </div>

            {/* Service modes — three pills under the CTAs */}
            <div
              className="mt-7 flex flex-wrap gap-2 animate-fade-up"
              style={{ animationDelay: "0.52s" }}
              data-testid="hero-service-modes"
            >
              {(t.hero.serviceModes || []).map((mode, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.06] border border-white/15 text-white/85 text-xs font-semibold tracking-wide"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-delfi-red" />
                  {mode}
                </span>
              ))}
            </div>

            <p
              className="mt-7 text-sm text-white/55 animate-fade-up"
              style={{ animationDelay: "0.6s" }}
              data-testid="hero-trust-line"
            >
              {t.hero.trust}
            </p>
          </div>

          {/* ----- Phone mockup column ----- */}
          <div
            className="relative flex justify-center lg:justify-end animate-fade-up"
            style={{ animationDelay: "0.4s" }}
            data-testid="hero-phone-column"
          >
            <PhoneMockup />
          </div>
        </div>

        {/* Bottom: full-width city map + notification feed */}
        <div
          className="mt-16 md:mt-20 relative animate-fade-up"
          style={{ animationDelay: "0.55s" }}
          data-testid="hero-map-container"
        >
          <AnimatedDelivery />
        </div>
      </div>

      {/* Bottom fade to next section */}
      <div
        className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-b from-transparent to-delfi-dark pointer-events-none"
        aria-hidden="true"
      />
    </section>
  );
}
